<script setup lang="ts">
import { useI18n } from '@/i18n';
import { useBillingStore, type CouponInterval } from '@/stores/billing';
import { Button, Input, LayoutGap, Modal } from '@dabble/quillui';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

// Absent Boolean props cast to false, so `trigger` needs an explicit default.
// `productId` is the plan in view, so a code for a different plan warns instead of celebrating.
// `interval` is the billing being checked out, so a code it can't take is refused up front.
// `gift` asks for a gift card, the only code a Lifetime checkout takes.
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    link?: boolean;
    trigger?: boolean;
    productId?: string;
    interval?: CouponInterval;
    gift?: boolean;
  }>(),
  { trigger: true },
);
const emit = defineEmits<{ applied: [] }>();

// Two-root template (Button + Modal) — forward parent attrs to the trigger Button explicitly.
defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const billingStore = useBillingStore();
const router = useRouter();

const showModal = ref(false);
const code = ref('');
const submitting = ref(false);

function open() {
  code.value = '';
  showModal.value = true;
}

function cancel() {
  showModal.value = false;
}

// Menus and other hosts can render their own trigger and open the modal directly.
defineExpose({ open });

async function apply() {
  const trimmed = code.value.trim();
  if (!trimmed) return;
  submitting.value = true;
  try {
    const ok = await billingStore.applyCouponCode(trimmed, { productId: props.productId, interval: props.interval });
    if (ok) {
      showModal.value = false;
      emit('applied');
      // A Lifetime gift card waits for its own checkout, which the store has pointed at its plan.
      if (billingStore.checkoutGift?.display === trimmed && router.currentRoute.value.name !== 'billing-checkout') {
        router.push({ name: 'billing-checkout' });
      }
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Button
    v-if="trigger"
    v-bind="$attrs"
    :variant="link ? 'secondary' : 'primary'"
    :finish="link ? 'link' : undefined"
    :disabled="disabled"
    @click="open"
  >
    {{ t(gift ? 'checkout_gift_apply' : 'billing_coupon_apply') }}
  </Button>

  <Modal v-model="showModal" size="small" :title="t(gift ? 'checkout_gift_apply' : 'billing_coupon_apply_title')">
    <LayoutGap direction="column" size="medium">
      <Input v-model="code" :label="t('prompt_coupon_code')" type="text" @keydown.enter="apply" />
      <LayoutGap direction="row" size="small" align="right">
        <Button variant="secondary" finish="outlined" :disabled="submitting" @click="cancel">
          {{ t('cancel') }}
        </Button>
        <Button variant="primary" :disabled="submitting || !code.trim()" @click="apply">
          {{ t('billing_coupon_apply') }}
        </Button>
      </LayoutGap>
    </LayoutGap>
  </Modal>
</template>
